import { cn } from '@/lib/utils'
import { MessageSquare, PlayCircle } from 'lucide-react'
import type { WATemplate } from '@/lib/api-client'

interface CampaignMessagePreviewProps {
  template: WATemplate | null
  mediaPreview?: string | null
  isVideo?: boolean
  className?: string
  emptyMessage?: string
}

export function CampaignMessagePreview({
  template,
  mediaPreview,
  isVideo,
  className,
  emptyMessage = 'Select a template to see the preview',
}: CampaignMessagePreviewProps) {
  return (
    <div
      className={cn(
        'rounded-xl bg-card p-4 sm:p-6 min-h-[300px] flex flex-col',
        className
      )}
    >
      {/* Header */}
      <div className="flex items-center gap-2 mb-4 pb-3 border-b border-border">
        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
          <MessageSquare className="w-4 h-4 text-primary" />
        </div>
        <div>
          <p className="text-xs font-medium text-text-dark">Message Preview</p>
          <p className="text-[10px] text-text-muted">
            {template ? template.name : 'No template selected'}
          </p>
        </div>
      </div>

      {!template ? (
        <div className="flex-1 flex items-center justify-center">
          <p className="text-sm text-text-muted text-center">{emptyMessage}</p>
        </div>
      ) : (
        <div className="flex-1 flex flex-col justify-end">
          {/* Message bubble */}
          <div className="max-w-[85%] ml-auto">
            {/* Media header */}
            {mediaPreview && (
              isVideo ? (
                <div className="relative">
                  <video
                    src={mediaPreview}
                    className="w-full h-40 object-cover rounded-t-lg"
                    muted
                    playsInline
                    onMouseEnter={(e) => (e.target as HTMLVideoElement).play()}
                    onMouseLeave={(e) => { const v = e.target as HTMLVideoElement; v.pause(); v.currentTime = 0 }}
                  />
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <PlayCircle className="w-10 h-10 text-white drop-shadow-lg" />
                  </div>
                </div>
              ) : (
                <img
                  src={mediaPreview}
                  alt="Campaign media"
                  className="w-full h-40 object-cover rounded-t-lg"
                />
              )
            )}

            {/* Body bubble */}
            <div
              className={cn(
                'bg-[#dcf8c6] px-3 py-2',
                mediaPreview ? 'rounded-b-lg' : 'rounded-lg'
              )}
            >
              {/* Header text if present */}
              {template.headerType === 'text' && template.headerContent && (
                <p className="text-sm font-semibold text-gray-900 mb-1">
                  {template.headerContent}
                </p>
              )}

              {/* Body */}
              <p className="text-[13px] text-gray-800 whitespace-pre-wrap leading-relaxed">
                {template.bodyContent}
              </p>

              {/* Footer */}
              {template.footerContent && (
                <p className="text-[11px] text-gray-500 mt-1">
                  {template.footerContent}
                </p>
              )}

              {/* Timestamp */}
              <div className="flex justify-end mt-1">
                <span className="text-[10px] text-gray-500">
                  {new Date().toLocaleTimeString('en-US', {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true,
                  })}
                </span>
              </div>
            </div>

            {/* Buttons */}
            {template.buttonContent &&
              Array.isArray(template.buttonContent) &&
              template.buttonContent.length > 0 && (
                <div className="mt-1 space-y-1">
                  {(
                    template.buttonContent as Array<{
                      text?: string
                      type?: string
                    }>
                  ).map((btn, idx) => (
                    <div
                      key={idx}
                      className="w-full p-2 text-center text-xs font-medium text-primary bg-primary/5 border border-border rounded-lg"
                    >
                      {btn.text || `Button ${idx + 1}`}
                    </div>
                  ))}
                </div>
              )}
          </div>
        </div>
      )}
    </div>
  )
}
